/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2016, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../../../widgets/livewidget/nls/StringResources',
	'ca-modeller/shaping',
	'../../../../lib/@waca/core-client/js/core-client/i18n/V5Formatter',
	'../../../../lib/@waca/dashboard-common/dist/ui/dialogs/Format/FormatConstants',
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../content/slotActions/api/SlotActionsProviderAPI',
	'../../../content/dataPointActions/api/DataPointActionsProviderAPI',
	'../../../../lib/@waca/dashboard-common/dist/api/ContentActionsProviderAPI'
], function(_, stringResources, Shaping, FormatUtils, FormatConstants, APIFactory, SlotActionsProviderAPI, DataPointActionsProviderAPI, ContentActionsProviderAPI) {

	return class FormatAction {

		constructor({ features, content }) {
			this.dashboard = features.API ? features.API : features['Dashboard.API'];
			this.content = content;
			this._features = features;
			this._dataPointActions = features.DataPointActions;
			this._slotActions = features.SlotActions;
			this._contentActions = features.ContentActions;

			this.locale = this._getPreferences('contentLocale');
			this.timezone = this._getPreferences('timeZoneID');

			// ids of format text input fields that will need to be set to metadata value if cleared
			this.textInputFields = ['defaultValue'];

			if(this.content) {
				this._dataPointActions.registerProvider('formatAction', this.getAPI());
				this._slotActions.registerProvider('formatAction', this.getAPI());
			} else {
				this._contentActions.registerProvider('formatAction', this.getAPI());
			}
		}

		_supportAction(dataSlot, itemIndex, visualization) {
			//Does not support multi select
			if(!dataSlot || !visualization.getDefinition().getProperty('supportsFormatAction')) {
				return false;
			}

			if (!itemIndex) {
				itemIndex = 0;
			}

			const dataItem = dataSlot.getDataItemList()[itemIndex];
			if (!dataItem) {
				return false;
			}

			const metadataColumn = dataItem.getMetadataColumn();
			if (!metadataColumn || metadataColumn.isMissing()) {
				return false;
			}

			return (this.dashboard.getMode() === this.dashboard.MODES.EDIT);
		}

		_supportDataPointAction(selections, visType) {
			const isAuthoringMode = this.dashboard.getMode() === this.dashboard.MODES.EDIT;
			if (isAuthoringMode) {
				const dataPoints = selections && selections.dataPoints;
				const facts = _.pluck(dataPoints, 'facts');

				// A data point should be selected
				if (facts.length > 0) {
					// For crosstab, are should not be edge
					if (visType === 'Crosstab' &&
						(selections.area === 'cell_edge_row' ||
						selections.area === 'cell_edge_column')) {
						return false;
					}
					return true;
				}
			}
			return false;
		}

		getDataPointActionList(selections) {
			let actionList = [];
			const visualization = this.content.getFeature('Visualization');
			if (this._supportDataPointAction(selections, visualization.getType())) {
				const dataPoints = selections.dataPoints || [];
				const categorySelections = dataPoints.length && _.flatten(_.without(_.pluck(dataPoints, 'categories'), undefined), true);
				const dataItem = categorySelections.length > 0 && categorySelections[0]
					&& visualization.getSlots().getMappingInfo(categorySelections[0].dataItemId).dataItem;
				let viewOptions = this._getViewOptions(dataItem, true /*isDataPoint*/, this.content);
				const format_16 = this._getIcons().getIcon('format16');

				actionList.push({
					name: 'formatAction',
					targetCaption: (dataItem && dataItem.getLabel()),
					label: stringResources.get('toolbarActionFormat'),	//The menu item text and label for tooltip are Format data
					viewCaption: stringResources.get('toolbarActionFormatText'),	//For datapoints, the view caption is always Format data (text)
					icon: format_16.id,
					text: stringResources.get('toolbarActionFormat'),
					type:'NextView',
					view: {
						module: 'dashboard-analytics/lib/@waca/dashboard-common/dist/ui/dialogs/Format/FormatDialog',
						state: viewOptions
					},
					actions: {
						apply: this.applyFormat.bind(this, this.content, selections)
					}
				});
			}
			return actionList;
		}

		/**
		 * @function getContentActionList
		 *
		 * @param {String[]} idList
		 * @return {Object[]} available content action list
		 */
		getContentActionList(idList) {
			if(idList.length === 1) {
				const contentId = idList[0];
				const content = this.dashboard.getCanvas().getContent(contentId);
				const visualization = content.getFeature('Visualization');
				if (visualization){
					const state = this._features['DashboardState'].getUiState();
					const actions = visualization.getDefinition().getProperty('actions');
					let supportAction = _.find(actions, (action) => { return action.id === 'FormatAction'; });
					if (supportAction && state.authoring && !state.eventGroups) {
						return this._getContribution(content, supportAction.slot, undefined, true);
					}
				}
			}
			return [];
		}

		_getIcons() {
			return this._features['Dashboard.Icons'] || this._features['Icons'];
		}

		_getContribution(content, dataSlotId, itemIndex, isContentAction) {
			const visualization = content.getFeature('Visualization');
			const dataSlot = visualization.getSlots().getSlot(dataSlotId);

			if(!this._supportAction(dataSlot, itemIndex, visualization)) {
				return [];
			}

			itemIndex = itemIndex === undefined ? 0 : parseInt(itemIndex);
			const dataItem = dataSlot.getDataItemList()[itemIndex];
			this.metadataFormat = this.getMetadataFormat(dataItem);
			let viewOptions = this._getViewOptions(dataItem, false /*isDataPoint*/, content);
			const format_16 = this._getIcons().getIcon('format16');
			const actionLabel = (dataItem && dataItem.getType()==='attribute') ? stringResources.get('toolbarActionFormatText') : stringResources.get('toolbarActionFormat');

			const contributon = {
				name: 'formatAction',
				targetCaption: dataItem.getLabel(),
				viewCaption: actionLabel,
				text: stringResources.get('toolbarActionFormat'),
				label: stringResources.get('toolbarActionFormat'),
				icon: format_16.id,
				section: ContentActionsProviderAPI.ACTION_SECTION.SELECTION_DATA,
				type: 'NextView',
				placement: 'top',
				view: {
					module: 'dashboard-analytics/lib/@waca/dashboard-common/dist/ui/dialogs/Format/FormatDialog',
					state: viewOptions
				},
				actions: {
					apply: this.applyFormat.bind(this, content, dataSlotId, itemIndex)
				}
			};

			if(isContentAction) {
				contributon.order = 80;
			}

			return [contributon];
		}

		getSlotActionList(dataSlotId, itemIndex) {
			const visualization = this.content.getFeature('Visualization');
			const dataSlot = visualization.getSlots().getSlot(dataSlotId);

			if(!this._supportAction(dataSlot, itemIndex, visualization)) {
				return [];
			}

			return this._getContribution(this.content, dataSlotId, itemIndex);
		}

		/**
		 *  Returns format types and format properties details, obtained from shaping
		 */
		_getFormatHelper() {
			if (this._formatHelper !== undefined) {
				return this._formatHelper;
			}

			this._formatHelper = {
				types: {},
				properties: {}
			};

			let formatTypeOptions = Shaping.formatUtils.getFormatTypes();
			let supportedTypes = FormatUtils.getSupportedV5Types();
			_.each(supportedTypes, supportedType => {
				this._formatHelper.types[supportedType] = _.find(formatTypeOptions.options, type => supportedType === type.value);
			});

			let formatPropertyOptions = Shaping.formatUtils.getFormatProperties();
			let supportedProps = FormatUtils.getSupportedV5Props();
			_.each(supportedProps, supportedProp => {
				this._formatHelper.properties[supportedProp] = _.find(formatPropertyOptions, property => supportedProp === property.property);
			});

			return this._formatHelper;
		}

		_getPreferences(ltInfo) {
			var userPreferences = this.dashboard.getGlassCoreSvc('.UserProfile').preferences;
			if (ltInfo === 'contentLocale') {
				return userPreferences.contentLocale || 'en-us';
			} else if (ltInfo === 'timeZoneID') {
				return userPreferences.timeZoneID;
			}
			return null;
		}

		_getViewOptions(dataItem, isDataPoint, content) {
			const viewOptions = this._getViewState(dataItem);
			/**
			 * todo: instead of providing callback function here, provide the actual Json obj.
			 *       Need to modify FormatDialog as well.
			 * e.g. 	options.metadataFormat = this.getMetadataFormat(dataSlotId, itemIndex)
			 *
			 */
			/** callback function here */
			viewOptions.getMetadataFormat =  this.getMetadataFormat.bind(this, dataItem);
			viewOptions.getDisabledItems = this.getDisabledItems.bind(this, dataItem);
			viewOptions.formatHelper = this._getFormatHelper();
			viewOptions.getDataItemFormat = this.getDataItemFormat.bind(this, dataItem);

			// don't show data item label for data point since the format will be applied to whole vis
			viewOptions.suppressSelectionLabel = isDataPoint;
			// for data point, the format will be applied to whole visualization
			viewOptions.visScope = isDataPoint;
			viewOptions.format = content.getPropertyValue('format') || {};

			return viewOptions;
		}

		_getViewState(dataItem) {
			let label = null;
			let dataType = null;
			if (typeof dataItem === 'object') {
				const binning = dataItem.getBinning && dataItem.getBinning() || null;
				label = binning && binning.label ? binning.label : dataItem.getLabel();
				dataType = dataItem.getDataType();
			}

			let options = {
				itemLabel: label,
				autoDescription: stringResources.get('formatAutoDescription'),
				defaultDescription: stringResources.get('formatDefaultDescription'),
				dataType: dataType,
				showBoxShadow: true // it surrounds the entire flyout not the subview
			};

			return options;
		}

		/**
		 * Returns the array of the item(s) that need to be disabled if Auto Binning is turned on
		 * @returns {Array} disabled Items - an array containing the items that need to be disabled in the format properties
		 */
		getDisabledItems(dataItem) {
			return dataItem.getBinning && dataItem.getBinning() ? ['formatAbbreviate'] : [];
		}

		/**
		 * Returns the metadata format of the data item that this format action corresponds to
		 * @returns {object} format spec - the metadata format spec
		 */
		getMetadataFormat(dataItem) {
			const metadataColumn = dataItem.getMetadataColumn();
			return metadataColumn && metadataColumn.getFormat() || {};
		}

		getDataItemFormat(dataItem) {
			let format = dataItem.getFormat && dataItem.getFormat() || null;
			if(format && !_.isEmpty(format)) {
				format.local = format.selectedFormat === 'none' ? false : true;
				return format;
			}
			return 'none';
		}

		/**
		 * Returns a string used to create unique type based keys in the spec
		 * @param {String} type  type of data format
		 * @param {String} string string representation of object key or setting
		 */
		_getOfType(type, string) {
			return `${type}_${string}`;
		}

		_getUseGrouping(spec, type){
			const useGroupingForType = this._getOfType(type, 'useGrouping');
			return _.isUndefined(spec[useGroupingForType]) ? {} : { useGrouping: spec[useGroupingForType] };

		}

		/**
		 * New style decimal size property, seperate the props depending on the setting type so settings dont leak across data format types in the formatter dialog. This function updates the spec
		 * - save all typed decimal size props
		 * - overwrite data item formatter expected values for current type
		 * @param {object} spec data format spec properties
		 * @param {string} type data format type
		 */
		_getDecimalSizeSpecForType (spec, type, dataItem) {
			let decimalFormatLengthKey = this._getOfType(type, 'decimalFormatLength'),
				maximumFractionDigitsKey = this._getOfType(type, 'maximumFractionDigits'),
				minimumFractionDigitsKey = this._getOfType(type, 'minimumFractionDigits'),
				oldResults = _.pick(spec, decimalFormatLengthKey, maximumFractionDigitsKey, minimumFractionDigitsKey),
				tempSpec = {};
			if (_.keys(oldResults).length === 0) {
				tempSpec = spec;
			} else {
				// use our typed spec to get the old decimal options and convert back
				tempSpec = _.extend(spec, {
					decimalFormatLength: oldResults[decimalFormatLengthKey],
					maximumFractionDigits: oldResults[maximumFractionDigitsKey],
					minimumFractionDigits: oldResults[minimumFractionDigitsKey]
				});
			}
			return this._getOldDecimalSizeSpec(tempSpec, dataItem);
		}

		/**
		 * Old style decimal size spec properties.
		 * This is also a fallback function which handles the auto case for types
		 */
		_getOldDecimalSizeSpec(spec, dataItem) {
			let decimalSpec = {};
			if (spec.decimalFormatLength === FormatConstants.FORMAT_LENGTH_SHORT || spec.maximumFractionDigits === FormatConstants.FORMAT_FRACTION_DIGITS_ABBREV) {
				decimalSpec = { decimalFormatLength: FormatConstants.FORMAT_LENGTH_SHORT };
			} else if (spec.maximumFractionDigits === 'auto') {
				// If set to auto, use the decimalSize from the MetadataColumn
				let metadataFormat = this.getMetadataFormat(dataItem);
				decimalSpec = { maximumFractionDigits: metadataFormat.maximumFractionDigits, minimumFractionDigits: metadataFormat.minimumFractionDigits };
			} else if (this._formatHelper.properties.decimalSize && (isNaN(parseInt(spec.maximumFractionDigits)))) {
				// When decimalSize is set to default, we do not perform any decimal formatting
				return decimalSpec;
			} else {
				decimalSpec = { maximumFractionDigits: parseInt(spec.maximumFractionDigits), minimumFractionDigits: parseInt(spec.maximumFractionDigits) };
			}
			return decimalSpec;

		}

		_getDecimalSizeSpec(spec, dataItem) {
			return this._getDecimalSizeSpecForType(spec.properties, spec.type, dataItem);
		}

		_getCurrencySpec(spec) {
			let currencySpec = {};
			// When currencyCode is set to default, we do not set a currency code in the format spec
			if (!this._isDefaultFormat(spec.currencyCode)) {
				currencySpec.currencyCode = spec.currencyCode;
			}
			if (!this._isDefaultFormat(spec.currencyDisplay)) {
				currencySpec.currencyDisplay = spec.currencyDisplay === 'false' ? 'symbol' : 'code';
			}
			if (spec.currencySymbol) {
				currencySpec.currencySymbol = spec.currencySymbol;
			}
			// Adding abbreviate option for Currency Type.
			let abbrvNumType = this._getOfType(FormatConstants.FORMAT_TYPES.CURRENCY, 'decimalFormatLength');
			if (spec[abbrvNumType] && spec[abbrvNumType] === FormatConstants.FORMAT_LENGTH_SHORT) {
				currencySpec.decimalFormatLength = FormatConstants.FORMAT_LENGTH_SHORT;
			}

			return currencySpec;
		}

		_getNumberLabelSpec(spec) {
			let numberLabelSpec = {};
			if (spec.numberLabel) {
				numberLabelSpec.numberLabel = spec.numberLabel;
				numberLabelSpec.numberLabelPosition = spec.numberLabelPosition;
			}
			return numberLabelSpec;
		}

		_isDefaultFormat(format) {
			return format === FormatConstants.FORMAT_DEFAULT_VALUE;
		}

		_getPercentSpec(spec) {
			return { convertPercent: spec.properties.convertPercent !== undefined ? spec.properties.convertPercent : spec.convertPercent };
		}

		_getDateTimeSpec(spec) {
			return { dateStyle: spec.dateTimeStyle };
		}

		_getDateSpec(spec) {
			return { dateStyle: spec.dateStyle };
		}

		_getTextSpec(spec) {
			return { defaultValue: spec.defaultValue };
		}

		/**
		 *  Consumes a spec from a FormatDialog and returns a spec consumable by Formatter, depending on format type.
		 * @param spec - the format spec to apply against the data item.
		 * @example FormatAction._getFormatSpec({
		 *  type: 'currency'
		 *  local: true,
		 *  properties: { currencyCode: 'EUR', currencyDisplay: 'symbol' }
		 * });
		 */
		_getFormatSpec(spec, dataItem) {
			let formatSpec = {
				local: spec.local,
				type: spec.type,
				timezone: this.timezone,
				locale: this.locale
			};

			if (this._formatHelper === undefined) {
				this._getFormatHelper(); // init formatHelper
			}

			if (spec.properties) {
				switch (spec.type) {
					case FormatConstants.FORMAT_TYPES.TEXT:
						_.extend(formatSpec, this._getTextSpec(spec.properties, spec.type));
						break;
					case FormatConstants.FORMAT_TYPES.NUMBER:
						_.extend(formatSpec, this._getDecimalSizeSpec(spec, dataItem), this._getUseGrouping(spec.properties, spec.type),
							this._getTextSpec(spec.properties, spec.type), this._getNumberLabelSpec(spec.properties));
						break;
					case FormatConstants.FORMAT_TYPES.CURRENCY:
						_.extend(formatSpec, this._getDecimalSizeSpec(spec, dataItem), this._getCurrencySpec(spec.properties),
							this._getUseGrouping(spec.properties, spec.type), this._getTextSpec(spec.properties, spec.type), this._getNumberLabelSpec(spec.properties));
						break;
					case FormatConstants.FORMAT_TYPES.PERCENT:
						_.extend(formatSpec, this._getDecimalSizeSpec(spec, dataItem), this._getPercentSpec(spec),
							this._getUseGrouping(spec.properties, spec.type), this._getTextSpec(spec.properties, spec.type));
						break;
					case FormatConstants.FORMAT_TYPES.DATETIME:
						_.extend(formatSpec, this._getDateTimeSpec(spec.properties), this._getTextSpec(spec.properties, spec.type));
						break;
					case FormatConstants.FORMAT_TYPES.DATE:
						_.extend(formatSpec, this._getDateSpec(spec.properties), this._getTextSpec(spec.properties, spec.type));
				}
			}
			return formatSpec;
		}

		/**
		 * If a text input field was submitted as undefined, set it to it's metadata value, if there is one
		 * @param formatSpec - the format spec submitted from the dialog
		 * @param metadataFormatSpec - the format spec set in the metadata
		 */
		_handleClearedTextInput(formatSpec, metadataFormat) {
			if (formatSpec && metadataFormat) {
				_.each(this.textInputFields, textInputField => {
					if (!formatSpec[textInputField] && metadataFormat[textInputField]) {
						formatSpec[textInputField] = metadataFormat[textInputField];
					}
				});
			}
		}

		/**
		 * This method is called by FormatDialog when an item is selected.
		 * @param spec - the format spec to apply against the data item.
		 */
		applyFormat(content, dataSlotId, itemIndex, spec) {
			let formatSpec;
			if (spec){
				const dataSlot = content.getFeature('Visualization').getSlots().getSlot(dataSlotId);
				const dataItem = dataSlot.getDataItemList()[itemIndex];
				formatSpec = spec.local ? this._getFormatSpec(spec, dataItem) : null;
				this._handleClearedTextInput(formatSpec, this.metadataFormat);
				dataItem.setFormat(formatSpec);
			} else {
				// when formatting is being applied to visualization instead of a slot arguments are shifted since a dataSlotId isn't provided
				spec = itemIndex;

				let clearSpec = true;
				// check if contains a non-undefined text input field in non-Auto mode
				_.each(this.textInputFields, textInputField => {
					if (spec.properties[textInputField] && spec.local) {
						clearSpec = false;
					}
				});

				formatSpec = clearSpec ? null : this._getFormatSpec(spec);
				// in this case add as a content property
				content.setPropertyValue('format', { formatSpec: formatSpec });
			}
		}

		getAPI() {
			if (!this._api) {
				if(this._contentActions) {
					this._api = APIFactory.createAPI(this, [ContentActionsProviderAPI]);
				} else {
					this._api = APIFactory.createAPI(this, [DataPointActionsProviderAPI, SlotActionsProviderAPI]);
				}
			}
			return this._api;
		}
	};
});
